import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { useMutation } from '@tanstack/react-query';
import { loginSchema } from "@tyto/shared";
import { Bird } from 'lucide-react';


type LoginFormValues = z.infer<typeof loginSchema>;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { mutate: login, isPending, isError, error } = useMutation({
    mutationKey: ['login'],
    mutationFn: async (data: LoginFormValues) => {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      if (res.ok) {
        return res.json()
      } else {
        throw new Error('Login failed')
      }
    },
    onSuccess: () => {
      navigate('/app');
    }
  })
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormValues) => {
    login(data)
  };

  return (
    <div className="min-h-[calc(100vh-10rem)] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Branding */}
        <div className="flex flex-col items-center gap-3 mb-8">
          <div className="p-3 bg-primary/10 rounded-xl">
            <Bird className="size-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">Sign in to TytoBio</h1>
          <p className="text-muted-foreground text-sm">
            Enter your credentials to access your account
          </p>
        </div>

        {/* Login form */}
        <Card className="shadow-lg border-primary/10">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  {...register('password')}
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="agreedToTerms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                />
                <Label htmlFor="agreedToTerms" className="text-sm font-normal leading-snug">
                  I agree to the{' '}
                  <Link to="/terms-of-service" className="text-primary hover:underline" target="_blank">Terms of Service</Link>,{' '}
                  <Link to="/privacy-policy" className="text-primary hover:underline" target="_blank">Privacy Policy</Link>{' '}and{' '}
                  <Link to="/cookies-policy" className="text-primary hover:underline" target="_blank">Cookie Policy</Link>
                </Label>
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting || !agreedToTerms}>
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            {isPending && <p className="text-sm text-muted-foreground mt-4">Logging in...</p>}
            {isError && <p className="text-sm text-destructive mt-4">{error.message}</p>}
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <div className="text-center text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline font-medium">
                Sign up
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );

};

export default Login;
